import { useNavigate } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import Input from "@/app/components/ui/Inputs/Input";
import { RegisterOptions, useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useConfigurationStaffCategoryUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffCategoryUpdateAPI";
import { useConfigurationStaffCategoryDetailsAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffCategoryDetailsAPI";
import { useConfigurationStaffCategoryOnboardAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffCategoryOnboardAPI";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

export interface IStaffCategoryOnboardFormFields {
  name: string;
  work: string;
  workMinutes: string;
  gap: string;
  gapMinutes: string;
}

const ConfigurationStaffCategoryOnboard = () => {
  const hasFetchedRef = useRef(false);
  const [isForUpdate, setIsForUpdate] = useState<boolean>(false);
  const [params, _setParams] = useSearchParams();
  const arrayConstants = useArrayConstants();
  const startTimeItem = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const startTimeMinuteItem = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];
  const navigate = useNavigate();
  const { newStaffCategoryId, execute: onboardStaffCategory } = useConfigurationStaffCategoryOnboardAPI();
  const { updatedStaffCategoryId, execute: updateStaffCategory } = useConfigurationStaffCategoryUpdateAPI();
  const { getRouteKey } = useRouter();
  const { staffCategoryDetails, execute: fetchStaffCategoryDetails } = useConfigurationStaffCategoryDetailsAPI();
  const { forName } = useFormValidations();

  const formHook = useForm<IStaffCategoryOnboardFormFields>({
    mode: "onChange",
  });

  const Submit = (data: IStaffCategoryOnboardFormFields) => {
    if (!isForUpdate) {
      onboardStaffCategory({
        category_name: data?.name,
        gap_duration: {
          hours: data?.gap,
          minutes: data?.gapMinutes,
        },
        working_duration: { hours: data?.work, minutes: data?.workMinutes },
      });
    } else {
      updateStaffCategory({
        category_id: `${staffCategoryDetails?.[0]?.id || params.get("staff_category_id")}`,
        category_name: data?.name,
        gap_duration: {
          hours: data?.gap,
          minutes: data?.gapMinutes,
        },
        working_duration: { hours: data?.work, minutes: data?.workMinutes },
      });
    }
  };

  useEffect(() => {
    if (staffCategoryDetails && staffCategoryDetails?.length > 0) {
      const staff = staffCategoryDetails[0];
      if (staff) {
        formHook.setValue("name", staff?.staff_category_name);
        formHook.setValue("gap", staff?.gap_time?.hours);
        formHook.setValue("gapMinutes", staff?.gap_time?.minutes);
        formHook.setValue("work", staff?.working_duration?.hours);
        formHook.setValue("workMinutes", staff?.working_duration?.minutes);
      }
    }
  }, [staffCategoryDetails]);

  useEffect(() => {
    if (isForUpdate) {
      if (params.get("staff_category_id") && !hasFetchedRef.current) {
        fetchStaffCategoryDetails(parseInt(params.get("staff_category_id")));
        hasFetchedRef.current = true;
      }
    }
  }, [isForUpdate]);

  useEffect(() => {
    if (newStaffCategoryId) {
      navigate(getRouteKey("STAFF_CATEGORY_SETUP", "url"));
    }
  }, [newStaffCategoryId]);

  useEffect(() => {
    if (updatedStaffCategoryId) {
      navigate(getRouteKey("STAFF_CATEGORY_SETUP", "url"));
    }
  }, [updatedStaffCategoryId]);

  useEffect(() => {
    const staffCategoryId = params?.get("staff_category_id");
    if (staffCategoryId) {
      setIsForUpdate(true);
    }
  }, [params?.get("staff_category_id")]);

  const clearError = (fieldName) => {
    formHook.clearErrors(fieldName);
  };

  const handleCancel = () => {
    formHook.reset();
    navigate(getRouteKey("STAFF_CATEGORY_SETUP", "url"));
  };

  return (
    <div className="bg-white py-1 rounded-md">
      <div className="text-black text-base font-semibold mt-2 mx-3">Staff Category Setup</div>
      <div className="mx-3">
        <form onSubmit={formHook.handleSubmit(Submit)}>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[200px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("name", {
                ...forName.validations,
                required: true,
              } as RegisterOptions<IStaffCategoryOnboardFormFields, "name">)}
              errorMessages={[forName.errors, { type: "required", message: "Name is required" }]}
              className="h-9 w-[300px] rounded-md border-slate-400"
              fieldError={formHook.formState.errors.name || null}
              placeholder="Enter Name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = e.target.value;
                const sanitizedValue = inputValue.replace(/[^A-Za-z\s]/g, "");
                formHook.setValue("name", sanitizedValue);
                clearError("name");
              }}
            />
          </div>
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[200px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Working Duration</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="flex gap-10">
              <div className="w-[100px]">
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "Working Hours is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("work", item?.title);
                      clearError("work");
                    }
                  }}
                  fieldError={formHook.formState.errors.work || null}
                  register={formHook.register("work", { required: true })}
                  selectItems={startTimeItem}
                  placeholder="HH"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={startTimeItem?.filter((oc) => oc?.title === formHook.watch("work"))?.[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-[130px]"
                />
              </div>
              <div className="w-[100px]">
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "Working Minutes is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("workMinutes", item?.title);
                      clearError("workMinutes");
                    }
                  }}
                  fieldError={formHook.formState.errors.workMinutes || null}
                  register={formHook.register("workMinutes", { required: true })}
                  selectItems={startTimeMinuteItem}
                  placeholder="MM"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={
                    startTimeMinuteItem?.filter((oc) => oc?.title === formHook.watch("workMinutes"))?.[0]
                  }
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-[130px]"
                />
              </div>
            </div>
          </div>
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[200px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Gap Duration</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="flex gap-10">
              <div className="w-[100px]">
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "Gap Hours is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook?.setValue("gap", item?.title);
                      clearError("gap");
                    }
                  }}
                  fieldError={formHook.formState.errors?.gap || null}
                  register={formHook.register("gap", { required: true })}
                  selectItems={[startTimeItem[0]]}
                  placeholder="HH"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={startTimeItem?.filter((oc) => oc?.title === formHook.watch("gap"))?.[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-[130px]"
                />
              </div>
              <div className="w-[100px]">
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "Gap Minutes is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook?.setValue("gapMinutes", item?.title);
                      clearError("gapMinutes");
                    }
                  }}
                  fieldError={formHook.formState.errors.gapMinutes || null}
                  register={formHook.register("gapMinutes", { required: true })}
                  selectItems={startTimeMinuteItem}
                  placeholder="MM"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={startTimeMinuteItem?.filter((oc) => oc?.title === formHook.watch("gapMinutes"))?.[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-[130px]"
                />
              </div>
            </div>
          </div>
          <div className="bg-white right-0 p-3 justify-end items-center gap-4 flex w-full border-t-2 bottom-0 fixed">
            <button className={SECONDARY_BUTTON} onClick={handleCancel}>
              Cancel
            </button>
            <input type="submit" className={`${PRIMARY_BUTTON} cursor-pointer`} value={"Save"} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigurationStaffCategoryOnboard;
